import { Service } from '@genial/services';
import type { ReportingEnumsReportingFilePeriodEnum } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { transformSortItem } from '../../utils/transformSortItem';

import { reportGeneratorDocumentTypeState, reportGeneratorPeriodState } from './atom';
import { mapReportGeneratorCategoriesFromApi, ReportGeneratorCategory } from './categories';
import { reportGeneratorScreenName } from './constants';
import { normalizeReportGeneratorList } from './normalize';
import { ReportGeneratorListResponse, ReportGeneratorQueryParams } from './types';

export const reportGeneratorDocumentTypeSelector = selector<string | undefined>({
  key: 'reportGeneratorDocumentTypeSelector',
  set: ({ set }, value) => {
    set(reportGeneratorDocumentTypeState, value as string);
  },
  get: ({ get }) => get(reportGeneratorDocumentTypeState),
});

export const reportGeneratorPeriodSelector = selector<
  ReportingEnumsReportingFilePeriodEnum | undefined
>({
  key: 'reportGeneratorPeriodSelector',
  set: ({ set }, value) => {
    set(reportGeneratorPeriodState, value as ReportingEnumsReportingFilePeriodEnum);
  },
  get: ({ get }) =>
    get(reportGeneratorPeriodState) as ReportingEnumsReportingFilePeriodEnum | undefined,
});

export const reportGeneratorCategoriesSelector = selector<ReportGeneratorCategory[]>({
  key: 'reportGeneratorCategoriesSelector',
  get: async () => {
    const { data } = await Service.ReportingServices.V1.getReportingFileDocumentTypes({
      screenName: reportGeneratorScreenName,
    });

    return mapReportGeneratorCategoriesFromApi(data);
  },
});

export const reportGeneratorListQuery = selectorFamily<
  ReportGeneratorListResponse,
  ReportGeneratorQueryParams
>({
  key: 'genialReport/reportGeneratorListQuery',
  get: ({ page, filtersToApply, orderBy, advisorSelected, partnerSelected }) => {
    return async ({ get }) => {
      const categories = get(reportGeneratorCategoriesSelector);
      const sortItems = transformSortItem(orderBy);

      const { data, meta } = await Service.ReportingServices.V1.getReportingFileFilter({
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName: reportGeneratorScreenName,
        limit: 10,
        ...filtersToApply,
        sortItems: sortItems && [sortItems],
        page,
      });

      const normalizedData = normalizeReportGeneratorList(data, categories);

      return { data: normalizedData, meta };
    };
  },
});
